import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { getClientInfo } from '@selectors/client';
import ExportModalContent from '@lib/inpulse/ExportModal';

/**
 * Displays a modal to handle batch exporting progressively.
 *
 * The component receives a list of item IDs to process, splits them into batches,
 * and uses a request function to fetch data for each batch. The fetched data is aggregated,
 * and a callback function is invoked once the export is complete.
 *
 * On success or failure, the modal title is updated accordingly.
 *
 * @param {Object} props
 * @param {Object} props.params
 * @param {string} props.params.title - Modal title.
 * @param {Array<string>} props.params.itemIds - List of item IDs to export.
 * @param {function(Array<string>): Promise<Array<unknown>>} props.params.requestFn - Function to fetch data given IDs.
 * @param {function(Array<unknown>, Array<unknown>): Array<Object>} [props.params.appendFn] - Optional function to merge
 * previously processed and newly fetched items. Depends on the result of requestFn.
 * @param {function(Array<unknown>): void} props.params.callbackFn - Function called when all items have been processed.
 *
 */
export const BatchExportModal = (props) => {
  const {
    params: { title, itemIds, requestFn, appendFn, callbackFn },
    closeModal,
  } = props;

  const BATCH_TO_FETCH_AT_ONCE = 50;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(title || '');
  const [itemIdsToProcess, setItemIds] = useState(itemIds);
  const [processedItems, setProcessedItems] = useState([]);

  useEffect(() => {
    loadByBatchOfIds([...itemIdsToProcess]);
  }, [itemIdsToProcess]);

  const loadByBatchOfIds = async (itemIds) => {
    if (!itemIds.length && !processedItems.length) {
      return;
    }

    // If no more ids, then all data has been fetched
    if (!itemIds.length) {
      setProgress(100);

      callbackFn(processedItems);

      exportReady(setLoading, setTitle);

      return;
    }

    const batchItemIds = itemIds.splice(0, BATCH_TO_FETCH_AT_ONCE);

    // Perform request
    try {
      const fetchedItems = await requestFn(batchItemIds);

      const itemsList = appendFn
        ? appendFn(processedItems, fetchedItems)
        : [...processedItems, ...fetchedItems];

      setProcessedItems(itemsList);

      updateProgress(itemIds, itemsList);

      setItemIds(itemIds);
    } catch {
      exportFailure(setLoading, setTitle);
    }
  };

  const updateProgress = (itemIds, processedItems) => {
    const productLeft = itemIds.length;
    const totalProducts = productLeft + processedItems.length;
    const updatedProgress = 100 - (productLeft / totalProducts) * 100;

    setProgress(updatedProgress);
  };

  const exitModal = (setLoading, closeModal) => {
    setLoading(false);
    closeModal();
  };

  const exportReady = (setLoading, setTitle) => {
    setTitle(i18next.t('GENERAL.EXPORT_SUCCESS'));
    setLoading(false);
  };

  const exportFailure = (setLoading, setTitle) => {
    setTitle(i18next.t('GENERAL.EXPORT_FAILURE'));
    setLoading(false);
  };

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps)(BatchExportModal);
